import React, { useState, useEffect, useContext } from 'react';
import MembershipFlowContext from '../../context/membership_flow_context';
import './newsletters.scss';
import Spinner from '../../../components/react/spinner/spinner';
import {
  getNewslettersForAA,
  toggleNewsletterSubscription,
} from '../../../account_newsletters/services/signed_in.api.service';
import { Newsletter, GetNewslettersResponse } from '../../../account_newsletters/types/types';
import chunk from 'lodash/chunk';
import AlertPopup from '../../../components/react/alert_popup/alert_popup';
import { AlertDataType } from '../../../components/react/alert_popup/types';
import { GENERIC_ERROR_MESSAGE } from '../../../components/react/alert_popup/constants';
import NewsletterEntry from './components/newsletter_entry/newsletter_entry';
import {
  filterNewsletters,
  extractActionableNewsletters,
  extractViewOnlyNewsletters,
} from './utils/newsletters.utils';
import { defineRedirectTo } from '../../utilities/email_registration.utilities';

const Newsletters: React.FC = () => {
  const { packageShortName, returnTo } = useContext(MembershipFlowContext);
  const [alertData, setAlertData] = useState<AlertDataType|null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [viewOnlyNewsletters, setViewOnlyNewsletters] = useState<Newsletter[]>([]);
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);

  const getNewsletters = (): Promise<void> =>
    getNewslettersForAA()
      .then((response: GetNewslettersResponse) => {
        const visibleNewsletters = filterNewsletters(response.data);
        setNewsletters(extractActionableNewsletters(visibleNewsletters));
        setViewOnlyNewsletters(extractViewOnlyNewsletters(visibleNewsletters));
      })
      .catch((): void => {
        setNewsletters([]);
        setViewOnlyNewsletters([]);
        setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE });
      })
      .finally((): void => setIsLoading(false));

  useEffect((): void => {
    getNewsletters();
  }, []);

  const onNewsletterToggle = (affectedNewsletter: Newsletter): Promise<Response> => {
    const { id, subscribed, permalink } = affectedNewsletter;
    const payload: { referrer: string } = {
      referrer: `${window.location.href}#${permalink}`,
    };

    return toggleNewsletterSubscription(affectedNewsletter, payload)
      .then((modifiedNewsletter): void => {
        // Because, on POST or DELETE, the BE does not return the `subscribed`
        // field for the newsletter, we have to assume that, on successful request,
        // the BE correctly toggled the `subscribed` field of the newsletter.
        modifiedNewsletter.subscribed = !subscribed;

        const updatedNewsletters = [
          ...newsletters.filter((newsletter) => newsletter.id !== id),
          modifiedNewsletter,
        ];

        setNewsletters(updatedNewsletters.sort((a, b) => a.position - b.position));
      })
      .catch((): void => {
        setAlertData({
          type: 'error',
          message: 'Your preferences couldn\'t be saved. Please try again.'
        });
      });
  };

  const renderSpinner: JSX.Element = (
    <Spinner isLoading={isLoading} isAbsolute={true} />
  );

  const renderActionableNewsletters: JSX.Element[] = chunk(newsletters, 2).map(
    (row: Newsletter[], index: number): JSX.Element => (
      <div key={index} className='row justify-content-around p-0'>
        {row.map(
          (newsletter: Newsletter, idx: number): JSX.Element => (
            <NewsletterEntry
              key={idx}
              newsletter={newsletter}
              viewOnly={false}
              onNewsletterToggle={onNewsletterToggle}
            />
          )
        )}
        {row.length === 1 && <div className='col-lg-5'></div>}
      </div>
    )
  );

  const renderViewOnlyNewsletters: JSX.Element[] = chunk(viewOnlyNewsletters, 2).map(
    (row: Newsletter[], index: number) => (
      <div key={index} className='row justify-content-around p-0 pb-lg-4'>
        {row.map(
          (newsletter: Newsletter, idx: number): JSX.Element => (
            <NewsletterEntry key={idx} newsletter={newsletter} viewOnly={true} />
          )
        )}
        {row.length === 1 && <div className='col-lg-5'></div>}
      </div>
    )
  );

  const renderNewsletters: JSX.Element = (
    <>
      <div className='title ps-0 ps-lg-4'>Stay up to date with Devex free newsletters</div>
      <p className='ps-0 ps-lg-4 mb-48'>
        Pick and choose from our range of free Devex newsletters and get the latest news, insider
        reporting and analysis delivered straight to your inbox.
      </p>
      <fieldset disabled={isLoading} className='border-0 p-0'>
        {renderActionableNewsletters}
      </fieldset>
      <p className='ps-0 ps-lg-4 mt-5 mb-45'>
        These are the newsletters you are already signed up to:
      </p>
      {renderViewOnlyNewsletters}
      <p className='ps-0 ps-lg-4 mb-lg-4 mt-lg-0 my-4'>
        You can opt out at any time through the newsletters&apos; management page.
      </p>
      <div className='btns-container text-center mt-4'>
        <a
          href={defineRedirectTo(packageShortName, returnTo)}
          className='btn btn-dark btn-lg mt-3 mb-3 text-white'
        >
          Continue
        </a>
      </div>
    </>
  );

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div className='react-newsletter-step mb-6' data-testid='newsletters-step'>
        {isLoading ? renderSpinner : renderNewsletters}
      </div>
    </>
  );
};

export default Newsletters;
